// Minimal customer-app auth context.
//
// Phase 5 keeps auth state local — no Redux / Zustand / Jotai. A React
// context holding the current user (or null) is enough for the shell. The
// context is hydrated by a single GET /api/me probe on app load (see
// RequireAuth); 401 means "not signed in" and triggers a redirect to /login.

import { createContext, useContext } from 'react';
import type { CurrentUserResponse } from '@shared/api-types';

export type AuthState =
  | { status: 'unknown' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: CurrentUserResponse };

export interface AuthContextValue {
  state: AuthState;
  /** Re-probe /api/me. Called after sign-in success and on demand. */
  refresh: () => Promise<void>;
  /** Local sign-out — clears state and pings POST /api/auth/logout. */
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside an <AuthProvider>.');
  }
  return ctx;
}
